import { LocalStorageItem } from '@coach-bot/data-access/shared/interfaces/local-storage-item.interface';
import { AbstractLocalStorageService } from '@coach-bot/data-access/shared/services/abstract/abstract-local-storage.service';
import { AppAction } from '@shared/extension/enums/app-action.enum';
import { DataType } from '@shared/extension/enums/data-type.enum';
import { Observable, Subscription } from 'rxjs';
import { isValidTab } from '@shared/extension/utils/tabs';
import { safeSendChromeMessageToTab, safeSendChromeRuntimeMessage } from '@shared/extension/utils/events';
import { ContentScriptAction } from '@shared/extension/enums/content-script-action.enum';

export class LocalStorageService extends AbstractLocalStorageService {
  private handleLocalStorageSubscription: Subscription | null = null;

  constructor() {
    super();
  }

  startHandlingLocalStorageChanges(): void {
    if (this.handleLocalStorageSubscription) {
      return;
    }
    this.handleLocalStorageSubscription = this.handleLocalStorageChanges().subscribe((item: LocalStorageItem) => {
      safeSendChromeRuntimeMessage({ action: AppAction.LocalStorageChanged, detail: { item } });
      chrome.tabs.query({}, function (tabs) {
        tabs.forEach(function (tab) {
          if (isValidTab(tab)) {
            safeSendChromeMessageToTab(tab.id!, {
              action: ContentScriptAction.LocalStorageChanged,
              detail: { item },
            });
          }
        });
      });
    });
  }

  stopHandlingLocalStorageChanges(): void {
    if (this.handleLocalStorageSubscription) {
      this.handleLocalStorageSubscription.unsubscribe();
      this.handleLocalStorageSubscription = null;
    }
  }

  async getLocalStorage<T = string>(key: string, valueType: DataType = DataType.String): Promise<T | undefined> {
    const data: { [key: string]: string } = await chrome.storage.local.get([key]);
    return this.getDataFromType(data[key], valueType) as T;
  }
  
  async setLocalStorage(key: string, value: unknown): Promise<void> {
    await chrome.storage.local.set({ [key]: this.getDataString(value) });
  }

  handleLocalStorageChanges(): Observable<LocalStorageItem> {
    return new Observable<LocalStorageItem>(observer => {
      chrome.storage.onChanged.addListener((changes, areaName) => {
        if (areaName !== 'local') {
          return;
        }
        for (const key in changes) {
          const item: LocalStorageItem = { key, value: changes[key].newValue };
          observer.next(item);
        }
      });
    });
  }
}
